import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';
import { Button, Row, Col, Card, CardBody } from 'reactstrap';
import { connect } from 'react-redux';
import renderToggleButtonField from 'src/shared/components/form/ToggleButton';
import renderSelectField from 'src/shared/components/form/Select';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import {  mobileNoFormat, mobileNoLength, validateDrivingLicenseNumber } from 'src/utils/validationUtils';
import DatePickerOnly from 'src/shared/components/form/DatePickerOnly';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import BusinessIcon from '@mui/icons-material/Business';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';

const required = value => value ? undefined : 'Required';

// Compact Input Field Renderer
const renderCompactField = ({ input, placeholder, type, meta: { touched, error, warning }, disabled, icon: Icon }) => (
    <div>
        <div className={`input-group input-group-sm ${touched && error ? 'has-validation' : ''}`}>
            {Icon && (
                <span className="input-group-text bg-light border-end-0 text-muted" style={{ padding: '0.25rem 0.5rem' }}>
                    <Icon style={{ fontSize: '1rem' }} />
                </span>
            )}
            <input
                {...input}
                className={`form-control form-control-sm ${touched && error ? 'is-invalid' : ''} ${Icon ? 'border-start-0' : ''}`}
                placeholder={placeholder}
                type={type}
                disabled={disabled}
                style={{ fontSize: '0.85rem' }}
            />
            {touched && ((error && <div className="invalid-feedback">{error}</div>) || (warning && <div className="text-warning small">{warning}</div>))}
        </div>
    </div>
);

const validateUsername = (value) => {
    if (!value) return 'Required';
    if (!/^[a-zA-Z0-9]{6,}$/.test(value)) {
        return 'Min 6 chars, no special characters.';
    }
    return undefined;
};

const DriverAddOrUpdate = ({
    handleSubmit,
    status,
    vehicleStatus,
    hubStatus,
    hubData,
    setLicenceExpiryDate,
    licenceExpiryDate
}) => {

    const [driverEmployer, setDriver] = useState([]);

    useEffect(() => {
        getAllDriverEmployerDetails();
    }, []);

    const getAllDriverEmployerDetails = async () => {
        try {
            const response = await PndRestApi.GetAllDriverEmployer();
            if (response.status === 200 && response.data.status === 200) {
                let driverList = response.data.data.map(item => ({ "label": item.employerName, "value": item.driverEmployerId }));
                setDriver(driverList);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="p-2 bg-light">

            {/* Agency & Account Section */}
            <Card className="border-0 shadow-sm mb-2">
                <CardBody className="p-2">
                    <div className="d-flex align-items-center mb-2 text-primary border-bottom pb-1">
                        <BusinessIcon style={{ fontSize: '1.2rem' }} className="me-2" />
                        <h6 className="m-0 fw-bold small text-uppercase">Agency & Account</h6>
                    </div>
                    <Row className="g-2">
                        <Col md={6}>
                            <div className="mb-1">
                                <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Leased Rider Agency <span className='text-danger'>*</span></label>
                                <Field
                                    validate={[required]}
                                    name="driverEmployerInfo"
                                    component={renderSelectField}
                                    options={driverEmployer}
                                    placeholder="Select Agency..."
                                />
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="mb-1">
                                <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Unique ID / Username <span className='text-danger'>*</span></label>
                                <Field
                                    validate={hubData ? required : [required, validateUsername]}
                                    component={renderCompactField}
                                    name="userName"
                                    type="text"
                                    placeholder="Username"
                                    disabled={!!hubData}
                                    icon={AccountCircleIcon}
                                />
                            </div>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {/* Personal & License Section */}
            <Card className="border-0 shadow-sm mb-2">
                <CardBody className="p-2">
                    <div className="d-flex align-items-center mb-2 text-primary border-bottom pb-1">
                        <BadgeIcon style={{ fontSize: '1.2rem' }} className="me-2" />
                        <h6 className="m-0 fw-bold small text-uppercase">Personal Details</h6>
                    </div>
                    <Row className="g-2">
                        <Col md={5}>
                            <div className="mb-1">
                                <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Driver Full Name <span className='text-danger'>*</span></label>
                                <Field
                                    validate={required}
                                    component={renderCompactField}
                                    name="driverName"
                                    type="text"
                                    placeholder="Full Name"
                                    icon={PersonIcon}
                                />
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="mb-1">
                                <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>License Number <span className='text-danger'>*</span></label>
                                <Field
                                    validate={[validateDrivingLicenseNumber]}
                                    component={renderCompactField}
                                    name="licenseNo"
                                    type="text"
                                    placeholder="License No."
                                    icon={BadgeIcon}
                                />
                            </div>
                        </Col>
                        <Col md={4}>
                    
                                <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>License Expiry <span className='text-danger'>*</span></label>
                                <div className="compact-datepicker">
                                    <DatePickerOnly
                                        setDate={setLicenceExpiryDate}
                                        name="licenseExpiry"
                                        value={licenceExpiryDate || ''}
                                    />
                                </div>
                            
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {/* Contact Information */}
            <Card className="border-0 shadow-sm mb-2">
                <CardBody className="p-2">
                    <div className="d-flex align-items-center mb-2 text-primary border-bottom pb-1">
                        <ContactPhoneIcon style={{ fontSize: '1.2rem' }} className="me-2" />
                        <h6 className="m-0 fw-bold small text-uppercase">Contact Info</h6>
                    </div>
                    <Row className="g-2">
                        <Col md={6}>
                            <div className="mb-1">
                                <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Mobile Number </label>
                                <Field
                                    name="mobileNo"
                                    component={renderCompactField}
                                    type="text"
                                    placeholder="97150xxxxxxx"
                                    validate={[ mobileNoFormat, mobileNoLength]}
                                    icon={PhoneIcon}
                                />
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="mb-1">
                                <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Email Address</label>
                                <Field
                                    name="emailId"
                                    component={renderCompactField}
                                    type="text"
                                    placeholder="mail@example.com"
                                    //validate={[emailErrorRequired]}
                                    icon={EmailIcon}
                                />
                            </div>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {/* Operational Status */}
            <Card className="border-0 shadow-sm mb-2">
                <CardBody className="p-2">
                    <div className="d-flex align-items-center mb-2 text-primary border-bottom pb-1">
                        <DriveEtaIcon style={{ fontSize: '1.2rem' }} className="me-2" />
                        <h6 className="m-0 fw-bold small text-uppercase">Assignments</h6>
                    </div>
                    <Row className="align-items-center g-2">
                        <Col md={4} className="border-end">
                            <div className="mb-0 text-center">
                                <label className="form-label fw-bold text-muted small mb-1 d-block" style={{ fontSize: '0.7rem' }}>ACTIVE STATUS</label>
                                <Field
                                    name="status"
                                    component={renderToggleButtonField}
                                    defaultChecked={status}
                                />
                            </div>
                        </Col>
                        <Col md={8}>
                            <div className="d-flex gap-2">
                                {vehicleStatus && (
                                    <div className="flex-grow-1">
                                        <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.7rem' }}>ASSIGNED VEHICLE</label>
                                        <div className="bg-white p-1 border rounded small text-truncate">
                                            <DriveEtaIcon style={{ fontSize: '0.9rem' }} className="me-1 text-muted" />
                                            {hubData?.vehicleModalName ? hubData?.vehicleModalName : 'N/A'}
                                        </div>
                                    </div>
                                )}
                                {hubStatus && (
                                    <div className="flex-grow-1">
                                        <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.7rem' }}>ASSIGNED HUB</label>
                                        <div className="bg-white p-1 border rounded small text-truncate">
                                            <BusinessIcon style={{ fontSize: '0.9rem' }} className="me-1 text-muted" />
                                            {hubData?.hubName ? hubData?.hubName : 'N/A'}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {/* Compact Action Bar */}
            <div className="d-flex justify-content-end pt-2 mt-2 border-top">
                <Button
                    color="primary"
                    type="submit"
                    size="sm"
                    className="px-4 py-1 rounded-pill shadow-sm d-flex align-items-center"
                    style={{ background: 'linear-gradient(90deg, #00C9FF 0%, #92FE9D 100%)', border: 'none', color: '#000', fontWeight: 'bold', fontSize: '0.85rem' }}
                >
                    <SaveIcon style={{ fontSize: '1rem' }} className="me-1" />
                    {hubData ? 'Update' : 'Save'}
                </Button>
            </div>
        </form>
    );
};

DriverAddOrUpdate.propTypes = {
    handleSubmit: PropTypes.func.isRequired,
};

const mapStateToProps = (state, data) => {
    if (data && data.hubData) {
        return {
            initialValues: {
                driverName: data.hubData.driverName,
                userName: data.hubData.userName,
                licenseNo: data.hubData.licenseNo,
                address: data.hubData.address,
                licenseExpiry: data.hubData.licenseExpiry,
                mobileNo: data.hubData.mobileNo,
                emailId: data.hubData.emailId,
                driverId: data.hubData.driverId,
                driverEmployerInfo: { "value": data.hubData?.employerId, "label": data.hubData?.employerName },
                modelName: data.hubData?.vehicleModalName ? data.hubData?.vehicleModalName + "( Plate No.-" + data.hubData?.vehiclePlateNo + ")" : data.hubData?.vehiclePlateNo,
                hubName: data.hubData?.hubName,
                status: data.hubData?.status
            }
        }
    }
    return {};
}

export default connect(mapStateToProps)(reduxForm({ form: 'HubAddOrUpdateForm', enableReinitialize: true })(DriverAddOrUpdate));